import {chunk} from '@benchling/js-utils';
import {Accordion, createStyles, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER, RED, WHITE} from 'ui-library/Colors';

import {FieldInfoText} from '../StepConfigEditorTypes';
import {getSectionNameOrNull, HelpButtonOrNull} from './SectionName';

export interface StepConfigField {
  variableName?: string;
  addIcon?: JSX.Element;
  editor: JSX.Element;
  infoText?: string | FieldInfoText;
  systemVariableName?: string;
  required?: boolean;
  banner?: JSX.Element;
}
interface StepConfigFieldsTemplateProps {
  sectionName?: string;
  fields: Array<StepConfigField>;
  numColumns: number;
  isCompact?: boolean;
  sectionInfoText?: string;
  isCollapsible?: boolean;
  banner?: JSX.Element;
  sectionAddIcon?: JSX.Element;
}

/** @package */
export function VariableHeaderOrNull({
  variableName,
  infoText,
  addIcon,
  isCompact,
  showRequiredAsterisk,
}: {
  variableName?: string;
  infoText?: string | FieldInfoText;
  addIcon?: JSX.Element;
  isCompact: boolean;
  showRequiredAsterisk?: boolean;
}): JSX.Element | null {
  const {classes} = useStyles();
  if (variableName == null) {
    return null;
  }
  const normalizedInfoText =
    typeof infoText === 'string' ? {text: infoText, displayMode: 'tooltip' as const} : infoText;
  return (
    <div className={classes.variableHeaderContainer}>
      <div className={css(stepConfigFieldsTemplateStyles.variableHeader)}>
        <span
          className={css(
            !isCompact && stepConfigFieldsTemplateStyles.variableText,
            isCompact && stepConfigFieldsTemplateStyles.compactVariableText
          )}
        >
          {variableName}
          {showRequiredAsterisk && (
            <span className={css(stepConfigFieldsTemplateStyles.requiredAsterisk)}> *</span>
          )}
        </span>
        {normalizedInfoText?.displayMode === 'tooltip' && (
          <HelpButtonOrNull infoText={normalizedInfoText.text} isCompact={isCompact} />
        )}
        {addIcon}
      </div>
      {normalizedInfoText?.displayMode === 'inline' && (
        <Text size="xs" color="dimmed" mt={-2}>
          {normalizedInfoText.text}
        </Text>
      )}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  variableHeaderContainer: {
    marginBottom: theme.spacing.xs,
  },
  content: {
    padding: 0,
  },
  chevron: {
    marginRight: 5,
  },
  control: {
    margin: 0,
    color: GRAY_DARKER,
    paddingLeft: 0,
  },
  item: {backgroundColor: WHITE, '&[data-active]': {backgroundColor: WHITE}},
}));

export function CollapsingSection({
  children,
  sectionHeader,
}: {
  children: React.ReactNode;
  sectionHeader: JSX.Element | null;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Accordion
      classNames={{
        content: classes.content,
        chevron: classes.chevron,
        control: classes.control,
        item: classes.item,
      }}
      variant="filled"
    >
      <Accordion.Item value="section">
        <Accordion.Control>{sectionHeader}</Accordion.Control>
        <Accordion.Panel>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function getColumnElements(
  rowFields: Array<StepConfigField>,
  numColumns: number,
  isCompact: boolean,
  rowIndex: number
): Array<JSX.Element> {
  return rowFields.map(({variableName, infoText, addIcon, editor, required, banner}, colIndex) => (
    <Col
      key={colIndex}
      sm={12 / numColumns}
      // For two-column displays, decrease the amount of padding between the two columns by reducing
      // the padding on the right of the first column and the padding on the left of the second column
      className={css(
        numColumns === 2 && colIndex % 2 === 0 && stepConfigFieldsTemplateStyles.smallPaddingRight,
        numColumns === 2 && colIndex % 2 === 1 && stepConfigFieldsTemplateStyles.smallPaddingLeft
      )}
      dataTestAttrs={{
        component: 'StepConfigFieldsTemplate',
        element: 'column',
        key: `row${rowIndex}`,
        index: `${colIndex}`,
      }}
    >
      <VariableHeaderOrNull
        variableName={variableName}
        showRequiredAsterisk={required}
        infoText={infoText}
        addIcon={addIcon}
        isCompact={isCompact}
      />
      {banner}
      {editor}
    </Col>
  ));
}

function getRowElements(
  fields: Array<StepConfigField>,
  numColumns: number,
  isCompact: boolean,
  sectionHeaderOrNull: JSX.Element | null,
  banner?: JSX.Element
): Array<JSX.Element> {
  return [...chunk(fields, numColumns)].map((rowFields, rowIndex) => (
    <Row
      key={rowIndex}
      className={css(!isCompact && stepConfigFieldsTemplateStyles.marginBottom)}
      dataTestAttrs={{component: 'StepConfigFieldsTemplate', element: 'row', index: `${rowIndex}`}}
    >
      {rowIndex === 0 && sectionHeaderOrNull && <Col sm={12}>{sectionHeaderOrNull}</Col>}
      {rowIndex === 0 && banner && <Col sm={12}>{banner}</Col>}
      {getColumnElements(rowFields, numColumns, isCompact, rowIndex)}
    </Row>
  ));
}

// TODO(BNCH-75415): Use this component to render taggedUnion editors.
export default function StepConfigFieldsTemplate({
  sectionName,
  fields,
  numColumns,
  isCompact = false,
  isCollapsible = false,
  sectionInfoText = undefined,
  banner,
  sectionAddIcon,
}: StepConfigFieldsTemplateProps): JSX.Element {
  const sectionNameOrNull = getSectionNameOrNull({sectionName, sectionInfoText, sectionAddIcon});
  if (isCollapsible) {
    return (
      <CollapsingSection sectionHeader={sectionNameOrNull}>
        <>{getRowElements(fields, numColumns, isCompact, null, banner)}</>
      </CollapsingSection>
    );
  }
  return <>{getRowElements(fields, numColumns, isCompact, sectionNameOrNull, banner)}</>;
}

/** @package */
export const stepConfigFieldsTemplateStyles = StyleSheet.create({
  variableHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  variableText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  requiredAsterisk: {
    color: RED,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  compactVariableText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  marginBottom: {
    marginBottom: 8,
  },
  smallPaddingRight: {
    paddingRight: 8,
  },
  smallPaddingLeft: {
    paddingLeft: 8,
  },
});
